/** Preset accent colors for worldbuilding entries. Stored as hex in frontmatter
 * `color` and reused by the graph canvas, storyboard arc chips, and peek cards. */
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
  toMaybe,
} from "./maybe";
import { DATABASE_ENTITY_TYPES } from "./entityFrontmatter";

export const ENTITY_ACCENT_COLORS = [
  { id: "rust", label: "Rust", value: "#7a4a2e" },
  { id: "amber", label: "Amber", value: "#9a7b4f" },
  { id: "forest", label: "Forest", value: "#3d5c40" },
  { id: "sea", label: "Sea", value: "#2a5c6a" },
  { id: "grape", label: "Grape", value: "#5c4a6a" },
  { id: "rose", label: "Rose", value: "#7a4a52" },
  { id: "slate", label: "Slate", value: "#4a5568" },
  { id: "charcoal", label: "Charcoal", value: "#3d3630" },
] as const;

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

type EntityAccentSurface = {
  background: string;
  borderColor: string;
  color: string;
  colorMuted: string;
};

/** Normalize a stored accent — accepts preset hex values only. */
export function normalizeEntityColor(value: Maybe<string>): Maybe<string> {
  return matchMaybe(value, {
    nothing: () => nothing,
    just: (raw) => {
      if (raw.length === 0) return nothing;
      const trimmed = raw.trim();
      if (trimmed.length === 0) return nothing;
      const lower = trimmed.toLowerCase();
      if (HEX_COLOR_RE.test(lower)) return just(lower);
      const preset = ENTITY_ACCENT_COLORS.find(
        (swatch) =>
          swatch.id === trimmed || swatch.value.toLowerCase() === lower,
      );
      return toMaybe(preset?.value);
    },
  });
}

/** Default accent for a built-in entity type — used when `color` is unset. */
export function defaultEntityTypeColor(type: Maybe<string>): Maybe<string> {
  return matchMaybe(type, {
    nothing: () => nothing,
    just: (typeId) => {
      const builtInM = toMaybe(
        DATABASE_ENTITY_TYPES.find((entry) => entry.id === typeId),
      );
      return isJust(builtInM)
        ? normalizeEntityColor(just(builtInM.value.color))
        : nothing;
    },
  });
}

/** Stored color, or the type default when the author hasn't picked one yet. */
export function entityEffectiveColor(
  color: Maybe<string>,
  type: Maybe<string>,
): Maybe<string> {
  const normalized = normalizeEntityColor(color);
  if (isJust(normalized)) return normalized;
  return defaultEntityTypeColor(type);
}

/** Shared tint recipe for chips, graph cards, and peek cards — subtle fill with
 * a stronger border and text biased toward `--text-strong` for contrast. */
function entityAccentSurface(accent: string): EntityAccentSurface {
  return {
    background: `color-mix(in oklch, ${accent} 26%, var(--bg-raised))`,
    borderColor: `color-mix(in oklch, ${accent} 78%, var(--line))`,
    color: `color-mix(in oklch, ${accent} 28%, var(--text-strong))`,
    colorMuted: `color-mix(in oklch, ${accent} 18%, var(--text-muted))`,
  };
}

function entityAccentCardStyle(
  color: Maybe<string>,
): Maybe<Record<string, string>> {
  const normalized = normalizeEntityColor(color);
  if (isNothing(normalized)) return nothing;
  const surface = entityAccentSurface(normalized.value);
  return just({
    background: surface.background,
    borderColor: surface.borderColor,
    color: surface.color,
  });
}

/** Inline styles for storyboard arc chips and similar compact badges. */
export function entityChipAccentStyle(
  color: Maybe<string>,
): Maybe<Record<string, string>> {
  return entityAccentCardStyle(color);
}

/** Inline styles for hover peek cards — quiet stone surface, accent on the
 * border only (scarce color; don’t wash the whole IntelliSense card). */
export function entityPeekAccentStyle(
  color: Maybe<string>,
): Maybe<Record<string, string>> {
  const normalized = normalizeEntityColor(color);
  if (isNothing(normalized)) return nothing;
  return just({
    borderColor: `color-mix(in oklch, ${normalized.value} 48%, var(--line))`,
  });
}

/** Very subtle text tint for entity name occurrences in manuscript prose. */
export function entityRefAccentAttributes(
  color: Maybe<string>,
): Maybe<Record<string, string>> {
  const normalized = normalizeEntityColor(color);
  if (isNothing(normalized)) return nothing;
  return just({
    style: `color: color-mix(in oklch, ${normalized.value} 42%, var(--text))`,
  });
}

/** Inline styles for graph canvas entity node bodies. */
export function entityGraphNodeBodyStyle(
  color: Maybe<string>,
  active = false,
): Maybe<Record<string, string>> {
  const normalized = normalizeEntityColor(color);
  if (isNothing(normalized)) return nothing;
  const surface = entityAccentSurface(normalized.value);
  return just({
    fill: surface.background,
    stroke: surface.borderColor,
    strokeWidth: active ? "2.5" : "1.5",
  });
}

/** Label fills for graph nodes that use an entry accent color. */
export function entityGraphNodeLabelStyle(
  color: Maybe<string>,
): Maybe<{ label: string; type: string }> {
  const normalized = normalizeEntityColor(color);
  if (isNothing(normalized)) return nothing;
  const surface = entityAccentSurface(normalized.value);
  return just({
    label: surface.color,
    type: surface.colorMuted,
  });
}
